import cx from 'classnames';
import React, {
    useState,
    useRef,
    UIEventHandler,
    createContext,
    useContext,
    ReactNode,
    useEffect,
} from 'react';
import { useIntl } from 'react-intl';

import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import { useWindowSize } from '../../../domains/Layout/BreakPoint/BreakPoint';
import { IconButton } from '../form/button/IconButton';
import { IconArrowLeft } from '../icons/ArrowLeft';
import { IconArrowRight } from '../icons/ArrowRight';

import style from './NaturalScrollCarousel.module.css';

interface NaturalScrollCarouselContextValue {
    scrollSnapAlign: 'start' | 'center';
}

const NaturalScrollCarouselContext = createContext<NaturalScrollCarouselContextValue | null>(null);

export function useNaturalScrollCarousel() {
    const context = useContext(NaturalScrollCarouselContext);
    if (!context) {
        throw new Error(
            'useNaturalScrollCarousel must be used within a <NaturalScrollCarousel> component',
        );
    }
    return context;
}

interface NaturalScrollCarouselProps {
    navScrollRatio?: number;
    scrollSnapAlign: 'start' | 'center';
    slidesGap?: string;
    children: ReactNode;
    trackingInformation: {
        navClick: Pick<TrackingProperties<typeof ampli.carouselClicked>, 'block'>;
    };
}

export function NaturalScrollCarousel({
    navScrollRatio = 0.8,
    scrollSnapAlign = 'center',
    slidesGap = '16px',
    trackingInformation,
    children,
}: NaturalScrollCarouselProps) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    const { width: windowWidth } = useWindowSize();
    const slidesContainerRef = useRef<HTMLDivElement>(null);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [slidesContainerScrollWidth, setSlidesContainerScrollWidth] = useState(
        slidesContainerRef.current?.scrollWidth ?? 0,
    );
    const [slidesContainerClientWidth, setSlidesContainerClientWidth] = useState(
        slidesContainerRef.current?.clientWidth ?? 0,
    );

    const isScrollAtStart = scrollLeft <= 0;
    const isScrollAtEnd = scrollLeft >= slidesContainerScrollWidth - slidesContainerClientWidth;

    const handleScroll: UIEventHandler<HTMLDivElement> = (event) => {
        setScrollLeft(event.currentTarget.scrollLeft);
    };

    const handlePrevious = () => {
        if (slidesContainerRef.current) {
            slidesContainerRef.current.scrollLeft -=
                slidesContainerRef.current.clientWidth * navScrollRatio;
        }

        trackEvent((ampli, defaultProperties) => {
            ampli.carouselClicked({
                ...defaultProperties,
                ...trackingInformation.navClick,
                direction: 'previous',
            });
        });
    };

    const handleNext = () => {
        if (slidesContainerRef.current) {
            slidesContainerRef.current.scrollLeft +=
                slidesContainerRef.current.clientWidth * navScrollRatio;
        }

        trackEvent((ampli, defaultProperties) => {
            ampli.carouselClicked({
                ...defaultProperties,
                ...trackingInformation.navClick,
                direction: 'next',
            });
        });
    };

    const contextValue: NaturalScrollCarouselContextValue = {
        scrollSnapAlign,
    };

    useEffect(() => {
        const slidesContainer = slidesContainerRef.current;
        if (!slidesContainer) {
            return;
        }
        setSlidesContainerScrollWidth(slidesContainer.scrollWidth);
        setSlidesContainerClientWidth(slidesContainer.clientWidth);
    }, [windowWidth]);

    return (
        <NaturalScrollCarouselContext.Provider value={contextValue}>
            <div
                className={style.container}
                /* eslint-disable-next-line react/forbid-dom-props */
                style={{ '--natural-scroll-carousel-slides-gap': slidesGap }}
            >
                <div
                    className={cx(style.navButtonWrapper, style.previousButton, {
                        [style.buttonHidden]: isScrollAtStart,
                    })}
                >
                    <IconButton
                        Icon={IconArrowLeft}
                        onClick={handlePrevious}
                        disabled={isScrollAtStart}
                        aria-label={formatMessage({ id: 'global.component.carousel.nav.previous' })}
                    />
                </div>

                <div
                    ref={(element) => {
                        slidesContainerRef.current = element;
                        if (element) {
                            if (slidesContainerScrollWidth !== element.scrollWidth) {
                                setSlidesContainerScrollWidth(element.scrollWidth);
                            }
                            if (slidesContainerClientWidth !== element.clientWidth) {
                                setSlidesContainerClientWidth(element.clientWidth);
                            }
                        }
                    }}
                    className={style.slides}
                    onScroll={handleScroll}
                >
                    {children}
                </div>

                <div
                    className={cx(style.navButtonWrapper, style.nextButton, {
                        [style.buttonHidden]: isScrollAtEnd,
                    })}
                >
                    <IconButton
                        Icon={IconArrowRight}
                        onClick={handleNext}
                        disabled={isScrollAtEnd}
                        aria-label={formatMessage({ id: 'global.component.carousel.nav.next' })}
                    />
                </div>
            </div>
        </NaturalScrollCarouselContext.Provider>
    );
}

interface NaturalScrollCarouselSlideProps {
    children: ReactNode;
}

function NaturalScrollCarouselSlide({ children }: NaturalScrollCarouselSlideProps) {
    const { scrollSnapAlign } = useNaturalScrollCarousel();

    return (
        <div
            // eslint-disable-next-line react/forbid-dom-props
            style={{ scrollSnapAlign }}
            className={style.slide}
        >
            {children}
        </div>
    );
}

NaturalScrollCarousel.Slide = NaturalScrollCarouselSlide;
